import { z } from "zod";
import { brewcontrolShowSchema, } from "./brewcontrol-catalog";
import { unitsCompatible } from "./telemetry-attributes";
function asStringMap(value) {
    if (typeof value !== "object" || value === null)
        return {};
    const out = {};
    for (const [k, v] of Object.entries(value)) {
        if (typeof v === "string")
            out[k] = v;
    }
    return out;
}
/**
 * Defensive: a shape it does not recognise yields "nothing exported" rather
 * than an exception on a status page.
 */
export function parseWiring(json) {
    if (!json)
        return null;
    let raw;
    try {
        raw = JSON.parse(json);
    }
    catch {
        return null;
    }
    // An array is an object too, and a JSON array parsed as a wiring yields one
    // with no controllers -- which reads downstream as "this node exports
    // nothing" rather than "this is not a wiring".
    if (typeof raw !== "object" || raw === null || Array.isArray(raw))
        return null;
    const o = raw;
    const controllers = {};
    if (typeof o.controllers === "object" && o.controllers !== null) {
        for (const [name, def] of Object.entries(o.controllers)) {
            if (typeof def !== "object" || def === null)
                continue;
            const d = def;
            controllers[name] = {
                type: typeof d.type === "string" ? d.type : "",
                inputs: asStringMap(d.inputs),
                setpoints: asStringMap(d.setpoints),
                outputs: asStringMap(d.outputs),
                state: asStringMap(d.state),
                label: typeof d.label === "string" ? d.label : null,
                show: d.show,
            };
        }
    }
    return {
        name: typeof o.name === "string" ? o.name : "",
        version: typeof o.version === "string" ? o.version : "",
        description: typeof o.description === "string" ? o.description : "",
        capture: Array.isArray(o.capture)
            ? o.capture.filter((c) => typeof c === "string")
            : [],
        controllers,
    };
}
// =============================================================================
// STRUCTURE
// =============================================================================
const bindingMap = z.record(z.string().min(1));
export const brewcontrolWiringControllerSchema = z
    .object({
    type: z.string().min(1),
    description: z.string().optional(),
    label: z.string().nullable().optional(),
    show: brewcontrolShowSchema.nullable().optional(),
    inputs: bindingMap.optional(),
    outputs: bindingMap.optional(),
    setpoints: bindingMap.optional(),
    state: bindingMap.optional(),
})
    .strict();
export const brewcontrolWiringSchema = z
    .object({
    name: z.string().min(1),
    version: z.string().min(1),
    description: z.string().optional(),
    capture: z.array(z.string()).optional(),
    controllers: z.record(brewcontrolWiringControllerSchema).optional(),
})
    .strict();
export const WIRING_BINDING_ROLES = [
    "inputs",
    "setpoints",
    "outputs",
    "state",
];
/** Strip the `outputs.` prefix a controller output carries; null if absent. */
export function outputAlias(path) {
    return path.startsWith("outputs.") ? path.slice("outputs.".length) : null;
}
const error = (path, message) => ({
    path,
    message,
    severity: "error",
});
/**
 * Everything checkable about a wiring from the text and the catalog alone.
 *
 * Structural first (the zod issues, unknown keys included), then the node's
 * own rules: output paths, one writer per alias, and per-type ports. Messages
 * are the ones the API has always returned; only the location is new.
 */
export function validateWiring(content, catalog) {
    let parsed;
    try {
        parsed = JSON.parse(content);
    }
    catch (e) {
        return [error([], `Not valid JSON: ${e.message}`)];
    }
    const structure = brewcontrolWiringSchema.safeParse(parsed);
    if (!structure.success)
        return structure.error.issues.map((i) => error(i.path, `${i.path.join(".") || "Wiring"}: ${i.message}`));
    const wiring = parseWiring(content);
    if (!wiring)
        return [error([], "Wiring must be a JSON object.")];
    const problems = [];
    if (!wiring.name)
        problems.push(error(["name"], "Wiring needs a name."));
    if (!wiring.version)
        problems.push(error(["version"], "Wiring needs a version."));
    const writers = new Map();
    for (const [name, def] of Object.entries(wiring.controllers)) {
        const spec = catalog?.[def.type];
        if (catalog && !spec) {
            problems.push(error(["controllers", name, "type"], `Controller '${name}': unknown type '${def.type}'.`));
        }
        for (const [port, path] of Object.entries(def.outputs)) {
            // The node requires a controller output to be a full `outputs.<alias>`
            // path while inputs and setpoints are bare names. That shape rule is not
            // in the controller catalog, so it is stated here.
            const key = outputAlias(path);
            const at = ["controllers", name, "outputs", port];
            if (key === null) {
                problems.push(error(at, `Controller '${name}' output '${port}': '${path}' must be 'outputs.<alias>'.`));
                continue;
            }
            const other = writers.get(key);
            if (other)
                problems.push(error(at, `Alias '${key}' is written by both '${other}' and '${name}'.`));
            writers.set(key, name);
        }
        if (spec)
            problems.push(...checkPorts(name, def, spec));
    }
    return problems;
}
function checkPorts(name, def, spec) {
    const problems = [];
    const roles = [
        ["input", "inputs", spec.inputs],
        ["setpoint", "setpoints", spec.setpoints],
        ["output", "outputs", spec.outputs],
    ];
    for (const [role, key, ports] of roles) {
        const bound = def[key];
        // A type with no declared ports of a role accepts any key set there.
        if ((role === "input" && spec.inputs_open) ||
            (role === "setpoint" && spec.setpoints_open))
            continue;
        if (ports.length === 0 &&
            spec.inputs_open === undefined &&
            spec.setpoints_open === undefined)
            continue;
        const known = new Set(ports.map((p) => p.name));
        for (const port of Object.keys(bound)) {
            if (!known.has(port))
                problems.push(error(["controllers", name, key, port], `Controller '${name}': unknown ${role} port '${port}'.`));
        }
        for (const port of ports) {
            if (port.required && !(port.name in bound)) {
                problems.push(error(["controllers", name, key], `Controller '${name}': ${role} port '${port.name}' is not bound.`));
            }
        }
    }
    const state = new Set(spec.state);
    for (const field of Object.keys(def.state)) {
        if (!state.has(field)) {
            problems.push(error(["controllers", name, "state", field], `Controller '${name}': '${field}' is not a state field of '${def.type}'.`));
        }
    }
    return problems;
}
/** True when a diagnostic sits at or below `prefix`. */
export function diagnosticUnder(diagnostic, prefix) {
    return (diagnostic.path.length >= prefix.length &&
        prefix.every((segment, i) => diagnostic.path[i] === segment));
}
/**
 * A port's declared unit against the alias it is bound to. Null when nothing
 * can be said (either side unknown) or the two agree; otherwise the message
 * the vessel preview also reports.
 */
export function bindingUnitMismatch(port, alias) {
    if (!port?.unit || !alias?.unit)
        return null;
    if (unitsCompatible(port.unit, alias.unit))
        return null;
    return `expected ${port.unit}, '${alias.alias}' uses ${alias.unit}.`;
}
/** Spellings of one unit the registry accepts as the same thing, folded to one. */
const UNIT_SPELLINGS = {
    C: "°C",
    celsius: "°C",
    percent: "%",
    pct: "%",
    seconds: "s",
};
/** The one spelling a unit is compared under: `C` and `celsius` are `°C`. */
export function canonicalUnitSpelling(unit) {
    if (!unit)
        return null;
    const trimmed = unit.trim();
    if (!trimmed)
        return null;
    return UNIT_SPELLINGS[trimmed] ?? trimmed;
}
/**
 * A setpoint port against the canonical unit of the parameter type bound to
 * it (decision 0001): agreement is identity, never conversion, so a port in
 * kilograms per hour is a mismatch against a type in grams per hour and the
 * fix lives on the device or wiring side. Null when either side is unknown or
 * they agree.
 */
export function setpointUnitMismatch(port, typeUnit) {
    const portUnit = canonicalUnitSpelling(port?.unit);
    const expected = canonicalUnitSpelling(typeUnit);
    if (!port || !portUnit || !expected)
        return null;
    if (portUnit === expected)
        return null;
    return `expected ${typeUnit}, port '${port.name}' is ${port.unit}.`;
}
const refVariant = (kind, names) => ({
    type: "object",
    properties: {
        [kind]: names?.length
            ? { type: "string", enum: names }
            : { type: "string" },
    },
    additionalProperties: false,
    required: [kind],
});
const nullable = (schema) => ({
    anyOf: [schema, { type: "null" }],
});
/**
 * The `show` sub-document, with refs restricted to one controller's ports
 * when a type is known. Mirrors `brewcontrolShowSchema`.
 */
function showSchema(spec) {
    const ref = {
        oneOf: [
            refVariant("input", spec?.inputs.map((p) => p.name)),
            refVariant("setpoint", spec?.setpoints.map((p) => p.name)),
            refVariant("output", spec?.outputs.map((p) => p.name)),
            refVariant("state", spec?.state),
        ],
    };
    const reading = {
        type: "object",
        properties: {
            of: ref,
            label: { type: ["string", "null"] },
            small: {
                type: "boolean",
                description: 'Render as a one-line "label value" instead of a labelled number.',
            },
        },
        additionalProperties: false,
        required: ["of"],
    };
    const status = {
        type: "object",
        properties: {
            of: ref,
            cases: {
                type: "array",
                items: {
                    type: "object",
                    properties: {
                        variant: { type: "string" },
                        text: { type: "string" },
                        reading: nullable(ref),
                    },
                    additionalProperties: false,
                    required: ["variant", "text"],
                },
            },
        },
        additionalProperties: false,
        required: ["of", "cases"],
    };
    const cascade = {
        type: "object",
        properties: {
            demand: ref,
            stages: {
                type: "array",
                items: {
                    type: "object",
                    properties: {
                        output: { type: "string" },
                        config: { type: "string" },
                    },
                    additionalProperties: false,
                    required: ["output", "config"],
                },
            },
        },
        additionalProperties: false,
        required: ["demand", "stages"],
    };
    return {
        type: "object",
        description: "What the dashboard shows for this controller.",
        properties: {
            headline: nullable(ref),
            detail: nullable(ref),
            target: nullable(ref),
            actuator: nullable(ref),
            readings: { type: "array", items: { type: "array", items: reading } },
            status: nullable(status),
            cascade: nullable(cascade),
        },
        additionalProperties: false,
    };
}
/**
 * Resolve a `<controller>.state.<tag>` input binding to the controller and the
 * state field it reads, the tag being the field's exported name if it has one.
 */
export function stateSource(wiring, binding) {
    const at = binding.indexOf(".state.");
    const def = at < 0 ? undefined : wiring.controllers[binding.slice(0, at)];
    if (!def)
        return null;
    const tag = binding.slice(at + ".state.".length);
    const field = Object.entries(def.state).find(([, name]) => name === tag);
    return { controller: binding.slice(0, at), field: field?.[0] ?? tag };
}
/** Every controller's published state tags, offered as input bindings. */
export function stateAliases(wiring, catalog) {
    return Object.entries(wiring?.controllers ?? {}).flatMap(([name, def]) => (catalog?.[def.type]?.state_descriptors ?? []).map((d) => ({
        alias: `${name}.state.${def.state[d.name] ?? d.name}`,
        unit: d.unit || null,
        direction: "input",
        readable: true,
    })));
}
function bindingSchema(ports, values, open) {
    return {
        type: "object",
        properties: Object.fromEntries(ports.map((port) => [
            port.name,
            {
                ...values,
                description: [port.label ?? port.name, port.unit && `(${port.unit})`]
                    .filter(Boolean)
                    .join(" "),
            },
        ])),
        additionalProperties: open ? values : false,
    };
}
/**
 * The JSON Schema a code editor validates and completes against.
 *
 * Without a catalog it is the node's static wiring schema. With one, every
 * controller becomes a `oneOf` over the known types so port names, alias
 * names and `show` refs complete per type.
 */
export function wiringJsonSchema(catalog, vocabulary = { aliases: [], attributes: [] }) {
    const aliasNames = [
        ...new Set(vocabulary.aliases.map((a) => a.alias)),
    ].sort();
    const attributeNames = vocabulary.attributes.map((a) => a.name);
    const alias = aliasNames.length
        ? { type: "string", enum: aliasNames }
        : { type: "string" };
    const outputPath = aliasNames.length
        ? {
            type: "string",
            enum: aliasNames
                .filter((a) => !a.includes("."))
                .map((a) => `outputs.${a}`),
        }
        : { type: "string", pattern: "^outputs\\." };
    const attribute = attributeNames.length
        ? { type: "string", enum: attributeNames }
        : { type: "string" };
    const anyBindings = {
        type: "object",
        additionalProperties: { type: "string" },
    };
    const common = (spec) => ({
        description: { type: "string" },
        label: { type: ["string", "null"] },
        show: nullable(showSchema(spec)),
    });
    const controller = catalog
        ? {
            oneOf: Object.entries(catalog)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([type, spec]) => ({
                type: "object",
                title: spec.label ?? type,
                description: spec.description,
                properties: {
                    type: { const: type, description: spec.description },
                    ...common(spec),
                    inputs: bindingSchema(spec.inputs, alias, !!spec.inputs_open),
                    setpoints: bindingSchema(spec.setpoints, attribute, !!spec.setpoints_open),
                    outputs: bindingSchema(spec.outputs, outputPath, false),
                    state: bindingSchema(spec.state.map((name) => ({ name, unit: "" })), attribute, false),
                },
                additionalProperties: false,
                required: ["type"],
            })),
        }
        : {
            type: "object",
            properties: {
                type: { type: "string" },
                ...common(),
                inputs: anyBindings,
                setpoints: anyBindings,
                outputs: { ...anyBindings, additionalProperties: outputPath },
                state: anyBindings,
            },
            additionalProperties: false,
            required: ["type"],
        };
    return {
        $schema: "https://json-schema.org/draft/2020-12/schema",
        title: "Wiring",
        type: "object",
        properties: {
            name: { type: "string" },
            version: { type: "string" },
            description: { type: "string" },
            capture: {
                type: "array",
                description: "Signal patterns to record; empty captures everything.",
                items: { type: "string" },
            },
            controllers: {
                type: "object",
                additionalProperties: controller,
                propertyNames: { pattern: "^[A-Za-z_][A-Za-z0-9_]*$" },
            },
        },
        additionalProperties: false,
        required: ["name", "version"],
    };
}
//# sourceMappingURL=brewcontrol-wiring.js.map